"use client";

import { useLocale } from "next-intl";

import { SearchSelect, type SearchSelectOption } from "@/components/ui";
import { getPathname, useRouter } from "@/i18n/navigation";

/**
 * The public catalog's typeahead — the shared `SearchSelect` over an inline
 * index of every published listing (value=sku, grouped by category). Picking
 * navigates straight to the item page; Escape/blur just close the list. The
 * input is a real GET field named `q`, so without JS Enter submits to the
 * catalog path and the grid filters server-side (`getRentalCatalog`) with the
 * same matcher the listbox uses.
 */
export function InventorySearch({
  options,
  label,
  placeholder,
  defaultQuery = "",
  className = "",
}: {
  /** The option index, built server-side by the page: value=sku, label=name,
   *  sub=manufacturer, group=categoryName, keywords covering all three. */
  options: SearchSelectOption[];
  /** Accessible name for the search landmark + field (no visible eyebrow). */
  label: string;
  placeholder: string;
  /** The active `?q=` filter, seeded back so the field reads what the grid
   *  is showing. */
  defaultQuery?: string;
  /** Layout only (width constraints). */
  className?: string;
}) {
  const locale = useLocale();
  const router = useRouter();

  return (
    <form
      method="get"
      role="search"
      aria-label={label}
      action={getPathname({ href: "/services/rentals/catalog", locale })}
      // Public floor for touch: hold the input at 16px under a coarse pointer
      // so focusing never zooms iOS (the office guards this via .office-app).
      className={`pointer-coarse:[&_input]:text-base ${className}`.trim()}
    >
      <SearchSelect
        label=""
        ariaLabel={label}
        placeholder={placeholder}
        options={options}
        name="q"
        defaultQuery={defaultQuery}
        blurOnPick
        onPick={(sku) =>
          router.push({
            pathname: "/services/rentals/catalog/[sku]",
            params: { sku },
          })
        }
      />
    </form>
  );
}
